/**
 * Import validation (spec section 32).
 *
 * The goal is to warn honestly, not to reject reasonable files. Only
 * genuinely unusable input is an error.
 */

import { LIMITS } from '../core/config.js';

export const ACCEPTED_VIDEO_TYPES = [
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/x-matroska',
  'video/ogg',
];

export const ACCEPTED_VIDEO_EXTENSIONS = ['.mp4', '.m4v', '.webm', '.mov', '.mkv', '.ogv'];

export const ISSUE_LEVELS = { ERROR: 'error', WARNING: 'warning', INFO: 'info' };

function issue(level, code, message) {
  return { level, code, message };
}

export function extensionOf(name = '') {
  const match = String(name).toLowerCase().match(/\.[a-z0-9]+$/);
  return match ? match[0] : '';
}

/**
 * Validate a candidate loop asset.
 *
 * @param {object} input
 * @param {string} [input.name]
 * @param {string} [input.type]        MIME type reported by the browser
 * @param {number} [input.size]        bytes
 * @param {number} [input.duration]    seconds, if probed
 * @param {number} [input.width]
 * @param {number} [input.height]
 * @param {string} [input.targetAspectRatio] e.g. '9:16'
 * @param {number} [input.maxBytes]
 * @returns {{ok:boolean, level:string, issues:Array, meta:object}}
 */
export function validateImport(input = {}) {
  const {
    name = '',
    type = '',
    size = 0,
    duration = null,
    width = null,
    height = null,
    targetAspectRatio = '9:16',
    maxBytes = LIMITS.maxImportBytes,
  } = input;

  const issues = [];
  const ext = extensionOf(name);
  const typeOk = ACCEPTED_VIDEO_TYPES.includes(String(type).toLowerCase());
  const extOk = ACCEPTED_VIDEO_EXTENSIONS.includes(ext);

  if (!typeOk && !extOk) {
    issues.push(
      issue(
        ISSUE_LEVELS.ERROR,
        'UNSUPPORTED_TYPE',
        `Unsupported file type "${type || ext || 'unknown'}". Use MP4, WebM or MOV.`,
      ),
    );
  } else if (!typeOk && extOk) {
    issues.push(
      issue(ISSUE_LEVELS.WARNING, 'MIME_MISMATCH', `The browser reported "${type || 'no type'}" but the file is ${ext}. Playback may vary.`),
    );
  }

  if (!size) {
    issues.push(issue(ISSUE_LEVELS.ERROR, 'EMPTY_FILE', 'The file is empty.'));
  } else if (size > maxBytes) {
    issues.push(
      issue(
        ISSUE_LEVELS.ERROR,
        'TOO_LARGE',
        `File is ${(size / 1024 / 1024).toFixed(0)} MB, over the ${(maxBytes / 1024 / 1024).toFixed(0)} MB import limit.`,
      ),
    );
  } else if (size > maxBytes * 0.4) {
    issues.push(
      issue(
        ISSUE_LEVELS.WARNING,
        'LARGE_FILE',
        `${(size / 1024 / 1024).toFixed(0)} MB is heavy for mobile browser storage. Consider a shorter or lower bitrate clip.`,
      ),
    );
  }

  if (duration !== null && Number.isFinite(duration)) {
    if (duration < LIMITS.minClipSeconds) {
      issues.push(
        issue(ISSUE_LEVELS.WARNING, 'TOO_SHORT', `Clip is ${duration.toFixed(1)}s. Anything under ${LIMITS.minClipSeconds}s repeats too often.`),
      );
    } else if (duration > LIMITS.maxClipSeconds) {
      issues.push(
        issue(
          ISSUE_LEVELS.WARNING,
          'TOO_LONG',
          `Clip is ${duration.toFixed(1)}s. ${LIMITS.idealClipSeconds[0]}-${LIMITS.idealClipSeconds[1]}s is the sweet spot for a loop source.`,
        ),
      );
    } else if (
      duration < LIMITS.idealClipSeconds[0] ||
      duration > LIMITS.idealClipSeconds[1]
    ) {
      issues.push(
        issue(
          ISSUE_LEVELS.INFO,
          'DURATION_OUTSIDE_IDEAL',
          `${duration.toFixed(1)}s is usable; ${LIMITS.idealClipSeconds[0]}-${LIMITS.idealClipSeconds[1]}s is ideal.`,
        ),
      );
    }
  } else {
    issues.push(issue(ISSUE_LEVELS.WARNING, 'DURATION_UNKNOWN', 'Could not read the clip duration in this browser.'));
  }

  if (width && height) {
    const actual = width / height;
    const [aw, ah] = String(targetAspectRatio).split(':').map(Number);
    const target = aw && ah ? aw / ah : 9 / 16;
    const delta = Math.abs(actual - target);
    if (delta > LIMITS.aspectTolerance) {
      issues.push(
        issue(
          ISSUE_LEVELS.WARNING,
          'ASPECT_MISMATCH',
          `Clip is ${width}x${height} (${actual.toFixed(2)}:1) but the target is ${targetAspectRatio}. It will be cropped to fit.`,
        ),
      );
    }
    if (Math.min(width, height) < 480) {
      issues.push(
        issue(ISSUE_LEVELS.WARNING, 'LOW_RESOLUTION', `${width}x${height} is low for a 1080x1920 output.`),
      );
    }
  } else {
    issues.push(issue(ISSUE_LEVELS.INFO, 'RESOLUTION_UNKNOWN', 'Resolution could not be read; it will be measured again on import.'));
  }

  const level = issues.some((i) => i.level === ISSUE_LEVELS.ERROR)
    ? ISSUE_LEVELS.ERROR
    : issues.some((i) => i.level === ISSUE_LEVELS.WARNING)
      ? ISSUE_LEVELS.WARNING
      : ISSUE_LEVELS.INFO;

  return {
    ok: !issues.some((i) => i.level === ISSUE_LEVELS.ERROR),
    level,
    issues,
    meta: { name, type, size, duration, width, height, extension: ext, targetAspectRatio },
  };
}

/**
 * Read real duration / dimensions from a File using a temporary video
 * element. Browser-only; returns nulls when probing is not possible.
 */
export function probeVideoFile(file, { timeoutMs = 15000 } = {}) {
  return new Promise((resolve) => {
    if (typeof document === 'undefined' || !(file instanceof Blob)) {
      resolve({ duration: null, width: null, height: null, probed: false, reason: 'no DOM' });
      return;
    }
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      video.removeAttribute('src');
      video.load?.();
      URL.revokeObjectURL(url);
      resolve(result);
    };
    const timer = setTimeout(
      () => finish({ duration: null, width: null, height: null, probed: false, reason: 'timeout' }),
      timeoutMs,
    );

    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.onloadedmetadata = () => {
      finish({
        duration: Number.isFinite(video.duration) ? video.duration : null,
        width: video.videoWidth || null,
        height: video.videoHeight || null,
        probed: true,
      });
    };
    video.onerror = () => finish({ duration: null, width: null, height: null, probed: false, reason: 'decode error' });
    video.src = url;
  });
}

/**
 * Full import pipeline: probe, validate, and return a decision the UI
 * can present without further interpretation.
 */
export async function assessImport(file, { targetAspectRatio = '9:16' } = {}) {
  const probe = await probeVideoFile(file);
  const validation = validateImport({
    name: file?.name ?? '',
    type: file?.type ?? '',
    size: file?.size ?? 0,
    duration: probe.duration,
    width: probe.width,
    height: probe.height,
    targetAspectRatio,
  });
  return { ...validation, probe };
}

/**
 * Loop quality is NEVER inferred (spec section 16).
 *
 * File metadata cannot tell you whether the first and last frame match,
 * so BEX LOOP always starts an import at "unknown" and lets the operator
 * mark it Seamless or Repeatable. Claiming "seamless" from a probe would
 * be exactly the kind of fake feature the spec forbids.
 */
export const DEFAULT_LOOP_QUALITY = 'unknown';

export function initialLoopQuality() {
  return DEFAULT_LOOP_QUALITY;
}
